/**
 * Shared dataset delete dialog (#972).
 *
 * Owns the whole delete interaction for both the Datasets list and the
 * DatasetDetails page, so neither has to duplicate the two-stage flow:
 *
 * - **Soft delete (default):** flips the registry row to inactive; reversible,
 *   the underlying DuckLake table is kept. This is the low-friction path.
 * - **Permanent delete (admin only):** a checkbox — rendered only when the
 *   current user is a superuser — escalates to a hard delete that also DROPs the
 *   physical table (#973). It is guarded by a type-the-dataset-name confirmation
 *   and sends `hard_delete=true`. The server independently restricts this to
 *   admins; the checkbox is the matching UI affordance.
 * - **Dependency guard:** if the dataset has downstream dependents the first
 *   call is rejected; the dialog then offers an explicit "Delete anyway" that
 *   retries with `force=true`, carrying the `hard_delete` choice through so a
 *   forced permanent delete stays permanent.
 */
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDeleteDataset } from '@/hooks/useDatasets';
import { useToast } from '@/hooks/useToast';
import { useAuthStore } from '@/store/auth';

interface DeleteDatasetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The dataset targeted for deletion; `null` renders nothing. */
  dataset: { id: string; name: string } | null;
  /** Called after a successful delete. `hardDeleted` distinguishes the paths. */
  onDeleted?: (result: { hardDeleted: boolean }) => void;
}

const DESTRUCTIVE_BTN =
  'bg-destructive text-destructive-foreground hover:bg-destructive/90';

/** The backend signals a dependency block via these phrases in its message. */
function isDependencyError(message: string): boolean {
  return (
    message.includes('downstream dependencies') || message.includes('force=true')
  );
}

function extractMessage(error: unknown): string {
  const withResponse = error as
    | { response?: { data?: { message?: string } } }
    | undefined;
  return (
    withResponse?.response?.data?.message ??
    (error instanceof Error ? error.message : 'Failed to delete dataset')
  );
}

export function DeleteDatasetDialog({
  open,
  onOpenChange,
  dataset,
  onDeleted,
}: DeleteDatasetDialogProps) {
  const { user } = useAuthStore();
  const canHardDelete = !!user?.is_superuser;
  const deleteMutation = useDeleteDataset();
  const { toast } = useToast();

  const [permanent, setPermanent] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [stage, setStage] = useState<'confirm' | 'force'>('confirm');

  // Reset local state each time the dialog opens for a (new) dataset, so a prior
  // permanent toggle or typed name never leaks into the next open. Adjusted
  // during render (React's "adjust state when a prop changes" pattern) instead
  // of an effect: `open` and `dataset?.id` mirror stable primitives (a boolean
  // and a string id), so this converges after exactly one extra render and
  // cannot loop.
  const [prevOpen, setPrevOpen] = useState(open);
  const [prevDatasetId, setPrevDatasetId] = useState(dataset?.id);
  if (open !== prevOpen || dataset?.id !== prevDatasetId) {
    setPrevOpen(open);
    setPrevDatasetId(dataset?.id);
    if (open) {
      setPermanent(false);
      setConfirmText('');
      setStage('confirm');
    }
  }

  if (!dataset) return null;

  const nameMatches = confirmText.trim() === dataset.name;
  const confirmDisabled =
    deleteMutation.isPending || (permanent && !nameMatches);

  const succeed = () => {
    toast({
      title: permanent ? 'Dataset Permanently Deleted' : 'Dataset Deleted',
      description: `Successfully deleted "${dataset.name}"`,
    });
    onOpenChange(false);
    onDeleted?.({ hardDeleted: permanent });
  };

  const fail = (error: unknown) => {
    toast({
      title: 'Delete Failed',
      description: extractMessage(error),
      variant: 'destructive',
    });
  };

  const handleConfirm = async () => {
    if (permanent && !nameMatches) return;
    try {
      await deleteMutation.mutateAsync({ id: dataset.id, hard_delete: permanent });
      succeed();
    } catch (error) {
      const message = extractMessage(error);
      if (isDependencyError(message)) {
        setStage('force');
      } else {
        fail(error);
      }
    }
  };

  const handleForce = async () => {
    try {
      await deleteMutation.mutateAsync({
        id: dataset.id,
        hard_delete: permanent,
        force: true,
      });
      succeed();
    } catch (error) {
      fail(error);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        {stage === 'confirm' ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete dataset</AlertDialogTitle>
              <AlertDialogDescription>
                Remove &quot;{dataset.name}&quot; from the dataset list. This is
                reversible — the dataset can be restored and its underlying table
                is kept.
              </AlertDialogDescription>
            </AlertDialogHeader>

            {canHardDelete && (
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="permanent-delete"
                    className="mt-0.5"
                    checked={permanent}
                    onCheckedChange={(checked) => setPermanent(checked === true)}
                  />
                  <Label
                    htmlFor="permanent-delete"
                    className="text-sm font-normal leading-snug"
                  >
                    Permanently delete — also drops the underlying DuckLake table.
                    This cannot be undone.
                  </Label>
                </div>

                {permanent && (
                  <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
                    <p className="text-sm text-destructive">
                      This permanently removes the registry entry <strong>and</strong>{' '}
                      drops the physical table for &quot;{dataset.name}&quot;. It
                      cannot be undone.
                    </p>
                    <Label htmlFor="confirm-name" className="text-sm">
                      Type the dataset name to confirm
                    </Label>
                    <Input
                      id="confirm-name"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder={dataset.name}
                      autoComplete="off"
                    />
                  </div>
                )}
              </div>
            )}

            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className={DESTRUCTIVE_BTN}
                disabled={confirmDisabled}
                onClick={(e) => {
                  e.preventDefault();
                  void handleConfirm();
                }}
              >
                {permanent ? 'Permanently delete' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Dataset has dependents</AlertDialogTitle>
              <AlertDialogDescription>
                {permanent
                  ? `"${dataset.name}" has downstream dependents. Deleting anyway will permanently remove it and drop its underlying table. This cannot be undone.`
                  : `"${dataset.name}" has downstream dependents. Deleting anyway will remove it and its dependent references.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className={DESTRUCTIVE_BTN}
                disabled={deleteMutation.isPending}
                onClick={(e) => {
                  e.preventDefault();
                  void handleForce();
                }}
              >
                Delete anyway
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
